// Capacity math — shared by CapacityPage (month heatmap) and the
// Dashboard's 2-week CapacityStrip. One computation, two renderers.
//
// HARD demand (colors the day): booked jobs — planned crew/trucks, because
// booking happens weeks before dispatch assigns anyone (see CapacityPage
// header comment for the full model).
// SOFT signals (markers, not load) — read from MOVES since 2026-09-18
// (moves-architecture.md: dates live on the move, options never carry them):
//   • quoting / quoted moves circling their load day — a day can LOOK open
//     while 3 quotes are hovering over it
//   • tentative dates on any live move (each date on its own — pack, load,
//     unload; the item says which)
//   • lead / walkthrough moves with a date ("Interested" — intake picked
//     the day, nothing priced yet)
//   • walkthrough appointments (estimator time, not crew/trucks)
// SUPPLY: active crew minus off-marked crew_availability rows; active
// trucks minus truck_downtime ranges covering the day. A day flagged
// `closed` (day_flags) has zero supply; `full` is a hard stop the
// renderers show as a tag (the day still has computed room).
//
// Old callers that pass only { jobs, estimates, clients, crew, trucks,
// availability } still work: with no `moves` (or an empty array) the soft
// signals fall back to the estimate / client mirror columns.

import { format, parseISO, addDays } from 'date-fns';
import { estimateExtraTrucks } from './constants';
import { kindIsHoldOnly } from './phaseKinds';
import { anyTentative, dateKeysOf, OPTION_LIVE, MOVE_TERMINAL } from './moves';

export const SOFT_ESTIMATE_STATUSES = new Set(['quoted', 'pending_approval', 'follow_up']);
// Move statuses that are "circling" a day: a quote in flight.
export const SOFT_MOVE_STATUSES = new Set(['quoting', 'quoted']);
// Move statuses that are merely "interested": a date picked, nothing priced.
export const INTEREST_MOVE_STATUSES = new Set(['lead', 'walkthrough']);
// Kept for callers that imported it; tentativeByDate now covers every
// non-terminal move (rule 3: a tentative date is a real date, whatever the
// status), so this Set is no longer consulted here.
export const TENTATIVE_MOVE_STATUSES = new Set(['quoted', 'booked', 'in_progress']);

const iso = (d) => format(d, 'yyyy-MM-dd');

// Every yyyy-mm-dd from a to b inclusive (capped so a typo'd year can't
// loop for ages). Used for truck downtime ranges.
function eachDay(from, to, cap = 400) {
    const out = [];
    let d;
    try { d = parseISO(from); } catch { return out; }
    if (Number.isNaN(d.getTime())) return out;
    const end = to || from;
    for (let i = 0; i < cap; i++) {
        const key = iso(d);
        if (key > end) break;
        out.push(key);
        d = addDays(d, 1);
    }
    return out;
}

export function computeCapacityData({ jobs, estimates, clients, crew, trucks, availability, moves = [], dayFlags = [], truckDowntime = [] }) {
    const activeCrewCount = (crew || []).filter(c => c.active !== false).length;
    const activeTruckCount = (trucks || []).filter(t => t.active !== false).length;
    // Moves drive the soft signals; the legacy estimate/client scan only
    // runs when there are none to read (pre-migration data, old callers).
    const useMoves = Array.isArray(moves) && moves.length > 0;

    const estimateById = new Map();
    for (const e of estimates || []) estimateById.set(e.id, e);
    const clientById = new Map();
    for (const c of clients || []) clientById.set(c.id, c);

    // date → { count, names } of off-marked crew
    const offByDate = new Map();
    for (const a of availability || []) {
        if (!a?.date || !String(a.status || '').includes('off')) continue;
        const entry = offByDate.get(a.date) || { count: 0, names: [] };
        entry.count += 1;
        const member = (crew || []).find(c => c.id === a.crew_id);
        if (member?.name) entry.names.push(member.name);
        offByDate.set(a.date, entry);
    }

    // date → hard load { crewNeeded, truckNeeded, laborHours, jobs: [...] }
    const loadByDate = new Map();
    // date → walkthroughs { count, items: [{ job, client }] }
    const walkthroughsByDate = new Map();
    for (const job of jobs || []) {
        if (!job?.move_date) continue;
        if (job.status === 'cancelled') continue;

        if (job.type === 'WALKTHROUGH') {
            const entry = walkthroughsByDate.get(job.move_date) || { count: 0, items: [] };
            entry.count += 1;
            entry.items.push({ job, client: clientById.get(job.client_id) || null });
            walkthroughsByDate.set(job.move_date, entry);
            continue;
        }

        // Ghost hold (phaseKinds.js, pick C 2026-09-17): a parked loaded
        // truck is TRUCK load only — no crew, no labor hours.
        const isHold = kindIsHoldOnly(job.phase_kind);
        const assignedCrew = Array.isArray(job.crew_ids) ? job.crew_ids.length : 0;
        const crewFor = isHold ? 0 : Math.max(assignedCrew, Number(job.crew_size) || 0);
        const assignedTrucks = Array.isArray(job.truck_ids) ? job.truck_ids.length : 0;
        const est = job.estimate_id ? estimateById.get(job.estimate_id) : null;
        const plannedTrucks = est ? 1 + estimateExtraTrucks(est).length : 0;
        const trucksFor = Math.max(assignedTrucks, plannedTrucks, 1);
        const hoursFor = crewFor * (Number(job.duration) || 0);

        const days = Math.max(1, Number(job.duration_days) || 1);
        let start;
        try { start = parseISO(job.move_date); } catch { continue; }
        for (let i = 0; i < days; i++) {
            const key = iso(addDays(start, i));
            const entry = loadByDate.get(key) || { crewNeeded: 0, truckNeeded: 0, laborHours: 0, jobs: [] };
            entry.crewNeeded += crewFor;
            entry.truckNeeded += trucksFor;
            entry.laborHours += hoursFor;
            entry.jobs.push({ job, crewFor, trucksFor, spanDay: i > 0 ? i + 1 : null, spanTotal: days });
            loadByDate.set(key, entry);
        }
    }

    // date → soft demand. MOVES (2026-09-18): a quoting / quoted move
    // hovers over its load day. Each item carries the move plus its first
    // live option (`estimate`, may be null while quoting) so readers can
    // still show hours / crew.
    const softByDate = new Map();
    const optionByMove = new Map(); // move_id → first live option (else first option)
    for (const e of estimates || []) {
        if (!e?.move_id) continue;
        const cur = optionByMove.get(e.move_id);
        if (!cur || (!OPTION_LIVE.has(cur.status) && OPTION_LIVE.has(e.status))) optionByMove.set(e.move_id, e);
    }
    const pushSoft = (date, item) => {
        const entry = softByDate.get(date) || { count: 0, items: [] };
        entry.count += 1;
        entry.items.push(item);
        softByDate.set(date, entry);
    };
    if (useMoves) {
        for (const m of moves) {
            if (!m?.load_date || !SOFT_MOVE_STATUSES.has(m.status)) continue;
            pushSoft(m.load_date, { estimate: optionByMove.get(m.id) || null, move: m, client: clientById.get(m.client_id) || null });
        }
    } else {
        // Legacy path (no moves passed): quoted-but-unbooked estimates.
        for (const e of estimates || []) {
            if (!e?.move_date) continue;
            if (!SOFT_ESTIMATE_STATUSES.has(e.status)) continue;
            pushSoft(e.move_date, { estimate: e, move: null, client: clientById.get(e.client_id) || null });
        }
    }

    // date → tentative dates aimed at that day. MOVES: every tentative date
    // (pack / load / unload, each on its own — rule 3) of any move that is
    // not completed / cancelled. `names` keeps the old shape; `items` adds
    // { move, client, key, label }.
    const tentativeByDate = new Map();
    const pushTentative = (date, name, extra) => {
        const entry = tentativeByDate.get(date) || { count: 0, names: [], items: [] };
        entry.count += 1;
        entry.names.push(name);
        entry.items.push(extra);
        tentativeByDate.set(date, entry);
    };
    if (useMoves) {
        for (const m of moves) {
            // Postponed (2026-09-21) = no date at all; its old tentative date
            // is not demand on that day.
            if (!m?.load_date || MOVE_TERMINAL.has(m.status) || m.status === 'postponed' || !anyTentative(m)) continue;
            const client = clientById.get(m.client_id) || null;
            for (const k of dateKeysOf(m)) {
                if (!k.tentative) continue;
                pushTentative(k.date, client?.name || 'Unknown', { move: m, client, key: k.key, label: k.label });
            }
        }
    } else {
        for (const c of clients || []) {
            if (!c?.date_tentative || !c?.move_date) continue;
            pushTentative(c.move_date, c.name || 'Unknown', { move: null, client: c, key: 'load', label: 'Load' });
        }
    }

    // date → INTERESTED (Jaylen 2026-07-29): intake picked this date but
    // nothing is quoted yet — the rung BELOW "Quote Pending". MOVES: a
    // `lead` or `walkthrough` move with a load date (neither has an option
    // in flight — the old "no jobs / no live estimate" exclusions are the
    // status itself). Items keep `client` and add `move`.
    const interestByDate = new Map();
    if (useMoves) {
        for (const m of moves) {
            if (!m?.load_date || !INTEREST_MOVE_STATUSES.has(m.status)) continue;
            const client = clientById.get(m.client_id) || null;
            if (client?.status === 'inactive') continue;
            const entry = interestByDate.get(m.load_date) || { count: 0, items: [] };
            entry.count += 1;
            entry.items.push({ client: client || { id: m.client_id, name: 'Unknown' }, move: m });
            interestByDate.set(m.load_date, entry);
        }
    } else {
        // Legacy path: clients with a move_date and no job / active estimate.
        const clientIdsWithJobs = new Set();
        for (const job of jobs || []) {
            if (job.type === 'WALKTHROUGH' || job.status === 'cancelled') continue;
            if (job.client_id) clientIdsWithJobs.add(job.client_id);
        }
        const clientIdsWithActiveEstimates = new Set();
        for (const e of estimates || []) {
            if (!e?.client_id) continue;
            if (SOFT_ESTIMATE_STATUSES.has(e.status) || e.status === 'booked' || e.status === 'completed') {
                clientIdsWithActiveEstimates.add(e.client_id);
            }
        }
        for (const c of clients || []) {
            if (!c?.move_date || c.date_tentative) continue;
            if (c.status === 'inactive') continue;
            if (clientIdsWithJobs.has(c.id) || clientIdsWithActiveEstimates.has(c.id)) continue;
            const entry = interestByDate.get(c.move_date) || { count: 0, items: [] };
            entry.count += 1;
            entry.items.push({ client: c, move: null });
            interestByDate.set(c.move_date, entry);
        }
    }

    // Day flags (day_flags table, 2026-09-18): date → { date, kind, note }.
    // `closed` = zero supply that day; `full` = a lead said stop selling it.
    const flagByDate = new Map();
    for (const f of dayFlags || []) { if (f?.date && f.kind) flagByDate.set(f.date, f); }
    const flagFor = (date) => flagByDate.get(date) || null;
    const isClosed = (date) => flagByDate.get(date)?.kind === 'closed';
    const isFull = (date) => flagByDate.get(date)?.kind === 'full';

    // Truck downtime (truck_downtime table): date → trucks out that day.
    // Only ACTIVE trucks count (an inactive truck isn't in the supply
    // number to begin with). downtimeItemsByDate keeps the rows for tooltips.
    const activeTruckIds = new Set((trucks || []).filter(t => t.active !== false).map(t => t.id));
    const truckById = new Map((trucks || []).map(t => [t.id, t]));
    const downtimeByDate = new Map();
    const downtimeItemsByDate = new Map();
    for (const row of truckDowntime || []) {
        if (!row?.truck_id || !row.from_date || !activeTruckIds.has(row.truck_id)) continue;
        for (const key of eachDay(row.from_date, row.to_date || row.from_date)) {
            downtimeByDate.set(key, (downtimeByDate.get(key) || 0) + 1);
            const items = downtimeItemsByDate.get(key) || [];
            items.push({ row, truck: truckById.get(row.truck_id) || null });
            downtimeItemsByDate.set(key, items);
        }
    }
    // Supply for a day: closed → 0; else active minus off / down.
    const truckSupplyFor = (date) => isClosed(date) ? 0 : Math.max(0, activeTruckCount - (downtimeByDate.get(date) || 0));
    const crewSupplyFor = (date) => isClosed(date) ? 0 : Math.max(0, activeCrewCount - (offByDate.get(date)?.count || 0));
    // One call for the renderers: { crewAvail, truckAvail, closed, full }.
    const supplyFor = (date) => ({ crewAvail: crewSupplyFor(date), truckAvail: truckSupplyFor(date), closed: isClosed(date), full: isFull(date) });
    const closedDates = new Set([...flagByDate.values()].filter(f => f.kind === 'closed').map(f => f.date));

    return {
        activeCrewCount, activeTruckCount, loadByDate, offByDate, softByDate, tentativeByDate, walkthroughsByDate, interestByDate,
        flagByDate, flagsByDate: flagByDate, flagFor, isClosed, isFull, closedDates, downtimeByDate, downtimeItemsByDate,
        truckSupplyFor, crewSupplyFor, supplyFor,
    };
}

export function loadTone(needed, avail) {
    if (!needed) return { bg: 'bg-white', bar: 'bg-slate-200' };
    const pct = avail > 0 ? needed / avail : 2;
    if (pct > 1) return { bg: 'bg-rose-50', bar: 'bg-rose-500' };
    if (pct > 0.8) return { bg: 'bg-amber-50', bar: 'bg-amber-500' };
    if (pct > 0.5) return { bg: 'bg-amber-50/40', bar: 'bg-amber-400' };
    return { bg: 'bg-emerald-50/60', bar: 'bg-emerald-500' };
}

// Worst-of the two axes — used for day backgrounds and strip dots.
export function dayPct(crewNeeded, crewAvail, truckNeeded, truckAvail) {
    const crewPct = crewAvail > 0 ? crewNeeded / crewAvail : (crewNeeded ? 2 : 0);
    const truckPct = truckAvail > 0 ? truckNeeded / truckAvail : (truckNeeded ? 2 : 0);
    return Math.max(crewPct, truckPct);
}

export function dayTone(crewNeeded, crewAvail, truckNeeded, truckAvail) {
    if (!crewNeeded && !truckNeeded) return 'bg-white hover:bg-slate-50';
    const worst = dayPct(crewNeeded, crewAvail, truckNeeded, truckAvail);
    if (worst > 1) return 'bg-rose-50 hover:bg-rose-100/70';
    if (worst > 0.8) return 'bg-amber-50 hover:bg-amber-100/70';
    if (worst > 0.5) return 'bg-amber-50/40 hover:bg-amber-50';
    return 'bg-emerald-50/60 hover:bg-emerald-50';
}
